"use client"
import { useRef, useState, useEffect } from "react"
import { Routes, Route } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Card from "./components/game/Card"
import Modal from "./Modal"
import NavBar from "./components/ui/NavBar"
import Confetti from "react-confetti"
import "./App.css"
import logo from "./assets/images/MATCHA.svg"
import leaderboardIcon from "./assets/icons/leaderboard.svg"
import SharePanel from "./components/modals/SharePanel"
import MailchimpModal from "./MailchimpModal"
import { useGame } from "./contexts/GameContext"
import { useAudio } from "./contexts/AudioContext"
import { LeaderboardProvider } from "./contexts/LeaderboardContext"
import LeaderboardModal from "./components/modals/LeaderboardModal"
import ReactionBanner from "./components/ui/ReactionBanner"
import AdminDashboard from "./pages/AdminDashboard"
import TutorialPage from "./pages/TutorialPage"
import Playtest from "./pages/Playtest"
import LanguageSwitcher from "./components/ui/LanguageSwitcher"
import MetaTags from "./components/SEO/MetaTags"
import StructuredData from "./components/SEO/StructuredData"

// Story Mode Components
import { StoryModeProvider, useStoryMode } from "./contexts/StoryModeContext"
import StoryLevelPicker from "./storyMode/components/StoryLevelPicker"
import { getLevelConfig } from "./storyMode/config/levelConfigs"
import { isFeatureEnabled } from "./utils/featureFlags"

// Tutorial Components
import AnimatedTutorial from "./components/tutorial/AnimatedTutorial"

// Session-persistent tutorial tracking (survives refresh, clears when tab is closed)
const _seenKey = "seenTutorials"
const seenTutorials = {
  has: (key) => {
    try { return JSON.parse(sessionStorage.getItem(_seenKey) || "[]").includes(key) } catch { return false }
  },
  add: (key) => {
    try {
      const arr = JSON.parse(sessionStorage.getItem(_seenKey) || "[]")
      if (!arr.includes(key)) { arr.push(key); sessionStorage.setItem(_seenKey, JSON.stringify(arr)) }
    } catch {}
  },
}

// Test helpers are available globally in development via dynamic import in useEffect

const ConfettiEffect = ({ confettiRunning }) => {
  const confettiAnchorRef = useRef(null)
  const [anchorPosition, setAnchorPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    if (confettiAnchorRef.current) {
      const rect = confettiAnchorRef.current.getBoundingClientRect()
      setAnchorPosition({
        x: rect.left,
        y: rect.top,
      })
    }
  }, [])

  return (
    <>
      <div
        ref={confettiAnchorRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100vw",
          height: "1px",
        }}
      />
      {confettiRunning && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={2000}
          gravity={0.05}
          recycle={false}
          confettiSource={{
            x: anchorPosition.x,
            y: anchorPosition.y,
            w: window.innerWidth,
            h: 0,
          }}
        />
      )}
    </>
  )
}

function App() {
  // i18n hook
  const { t } = useTranslation(['translation', 'game', 'modals']);

  // Get story mode progress from StoryModeContext
  const { currentLevelIndex, setCurrentLevelIndex, getCurrentLevelConfig, completeLevel, unlockLevel } = useStoryMode()

  // Get game state and functions from GameContext
  const {
    cards,
    flipped,
    matched,
    startTime,
    completionTime,
    elapsedTime,
    gameStarted,
    bestTime,
    difficulty,
    memoryMode,
    memoryGrid,
    timeRemaining,
    gameLost,
    showModal,
    showMemoryModal,
    confettiRunning,
    showInstructions,
    moveCount,
    moveHistory,
    isDailyChallenge,
    showDailyResults,
    dailyStreak,
    speedrunMode,
    showSharePanel,
    showPauseModal,
    gameComplete,
    gamePaused,
    pauseStartTime,
    totalPausedTime,
    dailyResultsVersion,
    isStoryMode,
    storyLevelIndex,
    storyLevelConfig,

    setShowModal,
    setShowMemoryModal,
    setIsDailyChallenge,
    setDifficulty,
    setMemoryMode,
    setMemoryGrid,
    setGameStarted,
    setSpeedrunMode,
    setShowInstructions,
    setShowSharePanel,
    setShowDailyResults,
    setGameComplete,
    setShowPauseModal,
    setGamePaused,
    setPauseStartTime,
    setTotalPausedTime,
    setCompletionTime,
    setMoveCount,
    setMoveHistory,
    setDailyStreak,
    setDailyResultsVersion,
    setIsStoryMode,
    setStoryLevelIndex,
    setStoryLevelConfig,

    handleClick,
    initializeGame,
    formatTime,
    formatBestTime,
    isDailyChallengeAvailable,
    restartGame,
    resumeGame,
    exitGame,
    closeSharePanel,
  } = useGame()

  // Get audio state and functions from AudioContext
  const { isMuted, toggleSound } = useAudio()

  // State for components specific to this component
  const [showMailchimpModal, setShowMailchimpModal] = useState(false)
  const [hasPlayedGame, setHasPlayedGame] = useState(() => {
    return localStorage.getItem("hasPlayedGame") === "true"
  })
  // Add the showLeaderboardModal state and handler to the App component
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false)
  const [leaderboardViewOnly, setLeaderboardViewOnly] = useState(false)

  // Story Mode State (managed by StoryModeContext)
  const [showStoryLevelPicker, setShowStoryLevelPicker] = useState(false)
  const [showStoryTutorial, setShowStoryTutorial] = useState(null) // null or level number

  // Animated Tutorial State
  const [showAnimatedTutorial, setShowAnimatedTutorial] = useState(null) // null or { mode, gridSize, constraints }

  // Debug timer position controls
  const [debugTimerPosition, setDebugTimerPosition] = useState({ left: 1233, top: 377 })
  const [showDebugControls, setShowDebugControls] = useState(false)

  // Load test helpers in development
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      import("./utils/test-helpers").catch(console.error);
    }
  }, [])

  // Debug: Log flipped state changes in story mode
  useEffect(() => {
    if (isStoryMode && process.env.NODE_ENV === 'development') {
      console.log('[App] Story mode flipped state:', flipped)
    }
  }, [flipped, isStoryMode])

  const [showBanner, setShowBanner] = useState(false)
  const [bannerText, setBannerText] = useState("")
  const [bannerAnimation, setBannerAnimation] = useState("default")

  // Enhanced banner tracking state
  const [consecutiveMatches, setConsecutiveMatches] = useState(0)
  const [wrongAttempts, setWrongAttempts] = useState(0) // Track actual wrong attempts for flawless detection
  const [gameEndBannerShown, setGameEndBannerShown] = useState(false)
  const [lastMatchedLength, setLastMatchedLength] = useState(0) // Track previous matched length
  const [lastWrongBannerTime, setLastWrongBannerTime] = useState(0) // Throttle wrong banners

  // Add ref to track banner timeout
  const bannerTimeoutRef = useRef(null)

  // Regular match messages to rotate through
  const regularMessages = [
    t('game:reactions.nice'),
    t('game:reactions.perfect'),
    t('game:reactions.amazing'),
    t('game:reactions.greatMatch')
  ]

  // Wrong match messages to rotate through
  const wrongMessages = [
    t('game:reactions.tryAgain'),
    t('game:reactions.oof'),
    t('game:reactions.almostThere'),
    t('game:reactions.mismatch'),
    t('game:reactions.lockIn')
  ]

  // Show Mailchimp modal after first game completion
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("Mailchimp modal check:", {
        hasPlayedGame,
        completionTime: completionTime !== null,
        showDailyResults,
        isDailyChallenge,
        isStoryMode
      })
    }

    // Only show for non-daily challenges, non-story mode, first time players
    if (!hasPlayedGame && completionTime !== null && !showDailyResults && !isDailyChallenge && !isStoryMode) {
      // Set flag immediately to prevent multiple triggers
      setHasPlayedGame(true)
      localStorage.setItem("hasPlayedGame", "true")

      setTimeout(() => {
        if (process.env.NODE_ENV === "development") console.log("Showing Mailchimp modal")
        setShowMailchimpModal(true)
      }, 3000) // Shows Mailchimp modal 3s after first game completion
    }
  }, [completionTime, hasPlayedGame, showDailyResults, isDailyChallenge, isStoryMode])

  // Helper functions
  const isMobileDevice = () => {
    return window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  }

  const handleSignupClick = (e) => {
    e.preventDefault()
    setShowMailchimpModal(true)
  }

  const handleDifficultyModalSignupClick = () => {
    setShowModal(false)
    setShowMailchimpModal(true)
  }

  const resetMailchimpModal = () => {
    localStorage.removeItem("mailchimpModalShown")
    setHasPlayedGame(false)
    if (process.env.NODE_ENV === "development") console.log("Mailchimp modal reset for testing")
  }

  // NavBar icon click handlers
  const handleIcon1Click = () => {
    // Instructions modal removed - tutorials now show automatically on first play
    // This function is kept for backward compatibility but does nothing
  }

  const handleIcon2Click = () => {
    if (!completionTime && !showDailyResults) {
      setGamePaused(true)
      setPauseStartTime(new Date())
      setShowPauseModal(true)
    } else {
      // Original difficulty selection behavior
      setGameStarted(false)
      setGameComplete(false)
      setShowModal(true)
    }
  }

  // Handle sharing results
  const handleShare = () => {
    if (completionTime === null) return
    setShowSharePanel(!showSharePanel)
  }

  // Add this function near other helper functions
  const checkIfGameSubmitted = () => {
    if (!completionTime) return false

    const submittedGames = JSON.parse(localStorage.getItem("submittedGames") || "[]")
    return submittedGames.some((id) => {
      const parts = id.split("-")
      return (
        parts[0] === localStorage.getItem("lastPlayerName") &&
        parts[1] === (isDailyChallenge ? "daily" : memoryMode ? "memory" : "standard") &&
        parts[2] === difficulty &&
        Math.abs(Number.parseFloat(parts[3]) - completionTime) < 0.1
      )
    })
  }

  // Function to open leaderboard modal
  const openLeaderboardModal = (viewOnly = false) => {
    setLeaderboardViewOnly(viewOnly)
    setShowLeaderboardModal(true)

    // If the game is in progress, pause it
    if (!completionTime && !showDailyResults && gameStarted) {
      setGamePaused(true)
      setPauseStartTime(new Date())
    }
  }

  // Check daily challenge and load daily challenge data
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0]
    const dailyScores = JSON.parse(localStorage.getItem("dailyScores") || "{}")
    const todayScore = dailyScores[today]

    // Check if we should show daily results
    const wasPlayingDaily = localStorage.getItem("wasPlayingDailyChallenge") === "true"
    const viewingResults = localStorage.getItem("viewingDailyResults") === "true"

    if (process.env.NODE_ENV === "development") console.log("Daily challenge check:", {
      todayScore,
      wasPlayingDaily,
      viewingResults,
      isDailyChallenge,
      showDailyResults,
    })

    if (todayScore && (wasPlayingDaily || viewingResults || isDailyChallenge)) {
      // Add a small delay to ensure all state is properly updated
      setTimeout(() => {
        setCompletionTime(todayScore.time)
        
        const savedMoveHistory = JSON.parse(localStorage.getItem("dailyMoveHistory") || "[]")
        setMoveHistory(savedMoveHistory.length > 0 ? savedMoveHistory : [])
        
        // Fallback for old daily challenges: use moveHistory.length if moves is 0 or undefined
        const storedMoves = todayScore.moves || 0
        const moves = storedMoves > 0 ? storedMoves : savedMoveHistory.length
        if (process.env.NODE_ENV === "development") {
          console.log("Daily challenge move count:", {
            savedMoves: todayScore.moves,
            historyLength: savedMoveHistory.length,
            finalMoves: moves
          })
        }
        setMoveCount(moves)

        const currentStreak = Number(localStorage.getItem("dailyStreak") || "1")
        setDailyStreak(currentStreak)

        setIsDailyChallenge(true)
        setShowDailyResults(true)
      }, 100)
    }

    // Clean up the flags after use
    return () => {
      if (showDailyResults) {
        localStorage.removeItem("wasPlayingDailyChallenge")
        localStorage.removeItem("viewingDailyResults")
      }
    }
  }, [dailyResultsVersion, isDailyChallenge, showDailyResults])

  const showReactionBanner = (text, animationType = "default", duration = 1200) => {
    if (process.env.NODE_ENV === "development") console.log("showReactionBanner called with:", text, animationType, "duration:", duration)

    // Prevent multiple banners at game end
    if (completionTime !== null && gameEndBannerShown) {
      if (process.env.NODE_ENV === "development") console.log("Game end banner already shown, skipping")
      return
    }

    // Clear any existing timeout to prevent conflicts
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current)
      bannerTimeoutRef.current = null
    }

    // Set the new banner immediately
    setBannerText(text)
    setBannerAnimation(animationType)
    setShowBanner(true)

    // Set new timeout and store the reference
    bannerTimeoutRef.current = setTimeout(() => {
      if (process.env.NODE_ENV === "development") console.log("Hiding banner:", text)
      setShowBanner(false)
      setBannerText("")
      setBannerAnimation("default")
      bannerTimeoutRef.current = null
    }, duration)
  }

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current)
      }
    }
  }, [])

  // Reset tracking when game starts
  useEffect(() => {
    if (gameStarted && !completionTime) {
      setConsecutiveMatches(0)
      setWrongAttempts(0)
      setGameEndBannerShown(false)
      setLastMatchedLength(0)
      setLastWrongBannerTime(0)
      if (process.env.NODE_ENV === "development") console.log("Game started - reset banner tracking")
    }
  }, [gameStarted, completionTime])

  // Track wrong attempts with throttled banners
  useEffect(() => {
    if (flipped.length === 2 && cards.length > 0 && cards[flipped[0]] !== cards[flipped[1]]) {
      if (process.env.NODE_ENV === "development") console.log("Wrong attempt detected")

      // Update wrong attempts and reset consecutive matches
      setWrongAttempts((prev) => prev + 1)
      setConsecutiveMatches(0)

      // Show wrong match banner with throttling (same as working "Try Again")
      const now = Date.now()
      if (now - lastWrongBannerTime > 1500) {
        // Reduced from 2000 to 1500ms
        const randomMessage = wrongMessages[Math.floor(Math.random() * wrongMessages.length)]
        if (process.env.NODE_ENV === "development") console.log("Showing wrong match banner:", randomMessage)
        showReactionBanner(randomMessage, "wrong", 900)
        setLastWrongBannerTime(now)
      }
    }
  }, [flipped, cards]) // Removed lastWrongBannerTime from dependencies since it's only used for comparison

  // Detect NEW matches only - with proper dependency management
  useEffect(() => {
    // Only trigger when matched length increases and is even (complete pair)
    if (matched.length > lastMatchedLength && matched.length % 2 === 0 && !completionTime) {
      const matchCount = matched.length / 2
      if (process.env.NODE_ENV === "development") console.log("NEW match detected! Count:", matchCount, "Previous matched length:", lastMatchedLength)

      // Update the last matched length immediately to prevent re-triggers
      setLastMatchedLength(matched.length)

      // Determine which banner to show based on current state (not updated state)
      const currentConsecutiveMatches = consecutiveMatches + 1

      if (currentConsecutiveMatches >= 3) {
        // 3+ consecutive matches = On Fire
        if (process.env.NODE_ENV === "development") console.log("On Fire! Consecutive matches:", currentConsecutiveMatches)
        showReactionBanner("On Fire! 🔥", "fire")
        setConsecutiveMatches(currentConsecutiveMatches)
      } else if (currentConsecutiveMatches >= 2) {
        // 2+ consecutive matches = Combo
        if (process.env.NODE_ENV === "development") console.log("Combo! Consecutive matches:", currentConsecutiveMatches)
        showReactionBanner("Combo! 💥", "combo")
        setConsecutiveMatches(currentConsecutiveMatches)
      } else if (matchCount === 1 && wrongAttempts === 0) {
        // First match with no wrong attempts = Speed Match
        if (process.env.NODE_ENV === "development") console.log("Speed Match! First try success")
        showReactionBanner("Speed Match! ⚡", "speed")
        setConsecutiveMatches(currentConsecutiveMatches)
      } else {
        // Regular match - rotate through messages
        const messageIndex = (matchCount - 1) % regularMessages.length
        const message = regularMessages[messageIndex]
        if (process.env.NODE_ENV === "development") console.log("Regular match:", matchCount)
        showReactionBanner(message)
        setConsecutiveMatches(currentConsecutiveMatches)
      }
    }
  }, [matched.length, lastMatchedLength, completionTime]) // Removed consecutiveMatches and wrongAttempts from dependencies

  // Enhanced game completion banners - prevent multiple banners
  useEffect(() => {
    if (completionTime !== null && !isDailyChallenge && !gameEndBannerShown) {
      if (process.env.NODE_ENV === "development") console.log("Game completed, showing final banner. Wrong attempts:", wrongAttempts)
      setGameEndBannerShown(true)

      // Check if it was a truly flawless run (no wrong attempts at all)
      const wasFlawless = wrongAttempts === 0

      if (bestTime !== null && completionTime < bestTime) {
        showReactionBanner("New Record!", "final")
      } else if (wasFlawless) {
        showReactionBanner("Flawless! ✨", "final")
      } else {
        showReactionBanner("Perfect!", "final")
      }
    }
  }, [completionTime, bestTime, isDailyChallenge, wrongAttempts, gameEndBannerShown])

  // Enhanced daily challenge completion - prevent multiple banners
  useEffect(() => {
    if (showDailyResults && completionTime !== null && !gameEndBannerShown) {
      if (process.env.NODE_ENV === "development") console.log("Daily challenge completed, showing banner. Wrong attempts:", wrongAttempts)
      setGameEndBannerShown(true)

      // Check if it was a perfect memory run (no wrong attempts)
      const wasMemoryMaster = wrongAttempts === 0

      if (wasMemoryMaster) {
        showReactionBanner("Memory Master! ✨", "final")
      } else {
        showReactionBanner("Daily Complete!", "final")
      }
    }
  }, [showDailyResults, completionTime, wrongAttempts, gameEndBannerShown])

  // Story Mode Handlers
  const handleStoryModeStart = (levelIndex) => {
    setCurrentLevelIndex(levelIndex)  // Set index first
    unlockLevel(levelIndex)  // Unlock this level when starting it
    const levelConfig = getCurrentLevelConfig()  // Then get config
    if (levelConfig) {
      // Clear previous game state
      setCompletionTime(null)
      setGameComplete(false)

      setDifficulty('medium')  // Set difficulty for story mode
      setMemoryMode(false)  // Ensure memory mode is off
      setIsDailyChallenge(false)  // Ensure daily challenge is off
      setIsStoryMode(true)
      setStoryLevelIndex(levelIndex)
      setStoryLevelConfig(levelConfig)
      setShowStoryLevelPicker(false)

      // Only show tutorial if it's the first level OR if constraints changed from previous level
      const shouldShowTutorial = (() => {
        if (levelIndex === 1) return true // Always show for first level

        // Check if already seen this tutorial during current visit
        const tutorialKey = `animatedTutorial_story_${levelIndex}`
        if (seenTutorials.has(tutorialKey)) return false

        // Get previous level config to compare
        const prevLevelConfig = getLevelConfig(levelIndex - 1)
        if (!prevLevelConfig) return true // Show if can't get prev config

        // Check if constraints changed
        const currentMoves = levelConfig.maxMoves || null
        const prevMoves = prevLevelConfig.maxMoves || null
        const currentTime = levelConfig.timeLimitSeconds || null
        const prevTime = prevLevelConfig.timeLimitSeconds || null

        // Show tutorial if moves or time constraints changed
        return currentMoves !== prevMoves || currentTime !== prevTime
      })()

      if (shouldShowTutorial) {
        seenTutorials.add(`animatedTutorial_story_${levelIndex}`)
        const constraints = {
          moves: levelConfig.maxMoves || null,
          timeLimit: levelConfig.timeLimitSeconds || null
        }

        setShowAnimatedTutorial({
          mode: 'story',
          gridSize: { rows: levelConfig.rows, cols: levelConfig.cols },
          constraints,
          levelIndex
        })
      } else {
        // Start game directly - no rule changes
        initializeGame()
      }
    }
  }

  const handleStoryModeExit = () => {
    setIsStoryMode(false)
    setStoryLevelConfig(null)
    setGameStarted(false)
  }

  const handleStoryLevelComplete = () => {
    completeLevel()
    // Check if there's a next level
    const nextLevel = currentLevelIndex + 1
    if (nextLevel <= 30) {
      setCurrentLevelIndex(nextLevel)
    }
  }

  return (
    <div className={`App ${showDailyResults ? "daily-results-active" : ""}`}>
      {/* SEO Meta Tags */}
      <MetaTags page={isDailyChallenge ? 'daily' : 'home'} />
      <StructuredData />

      {/* Only show confetti on win screen, not during gameplay */}
      {confettiRunning && !gameStarted && <ConfettiEffect confettiRunning={confettiRunning} />}

      {showBanner && <ReactionBanner text={bannerText} animationType={bannerAnimation} />}

      {/* Story Mode uses the same game UI as regular modes */}
      {/* Just add story mode info overlay when active */}
          {gameStarted && (
            <NavBar
              onIcon1Click={handleIcon1Click}
              onIcon2Click={handleIcon2Click}
              onIcon3Click={toggleSound}
              onRestartClick={restartGame}
              onLeaderboardClick={() => openLeaderboardModal(true)} // Always view-only from navbar
              isMuted={isMuted}
            />
          )}
          {!gameStarted && <img src={logo || "/placeholder.svg"} alt={t('nav.logoAlt')} className="logo" />}
          {!gameStarted && <h2 className="landing-subheader">{t('landing.subtitle')}</h2>}

          {!gameStarted && !gameLost && (
            <div className="landing-btn-group">
              <button onClick={() => setShowModal(true)} className="start-game-btn">
                {t('common.play')}
              </button>
              <button onClick={() => openLeaderboardModal(true)} className="landing-leaderboard-btn">
                <img src={leaderboardIcon} alt="" width="18" height="18" />
                {t('modals:leaderboard.title') || 'Leaderboard'}
              </button>
            </div>
          )}

          {/* Expandable About section - Collapsed by default, SEO-rich content */}
          {!gameStarted && !gameLost && (
            <details className="about-section">
              <summary>{t('seo:about.title')}</summary>
              <div className="about-content">
                {t('seo:about.content').split('\n\n').map((paragraph, index) => {
                  // Check if this is a heading (starts with **)
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    const headingText = paragraph.replace(/\*\*/g, '');
                    return <h3 key={index}>{headingText}</h3>;
                  }
                  // Check if this is a bullet point line (starts with -)
                  if (paragraph.includes('\n-')) {
                    const lines = paragraph.split('\n');
                    const beforeBullets = [];
                    const bullets = [];
                    let inBullets = false;

                    lines.forEach(line => {
                      if (line.trim().startsWith('-')) {
                        inBullets = true;
                        bullets.push(line.trim().substring(1).trim());
                      } else if (line.trim()) {
                        if (!inBullets) {
                          beforeBullets.push(line);
                        }
                      }
                    });

                    return (
                      <div key={index}>
                        {beforeBullets.length > 0 && <p>{beforeBullets.join(' ')}</p>}
                        <ul>
                          {bullets.map((bullet, i) => {
                            // Handle nested bold text within bullets
                            const parts = bullet.split('**');
                            return (
                              <li key={i}>
                                {parts.map((part, j) =>
                                  j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  }
                  // Regular paragraph
                  return <p key={index}>{paragraph}</p>;
                })}
              </div>
            </details>
          )}

          {gameStarted && !completionTime && (
            <div className={`game-container ${isStoryMode ? 'story-mode-container' : ''}`}>
              {/* Story Mode Level Info */}
              {isStoryMode && storyLevelConfig && (
                <div className="story-mode-info">
                  <h3>Level {storyLevelIndex}</h3>
                  {storyLevelConfig.constraintMode !== 'none' && (
                    <div className="story-constraints">
                      {(storyLevelConfig.constraintMode === 'moves' || storyLevelConfig.constraintMode === 'movesAndTime') && (
                        <span className={moveCount >= storyLevelConfig.maxMoves * 0.8 ? 'danger' : ''}>
                          Moves: {moveCount}/{storyLevelConfig.maxMoves}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className={`grid ${
                isStoryMode && storyLevelConfig
                  ? `story-grid-${storyLevelConfig.rows}x${storyLevelConfig.cols}`
                  : memoryMode
                    ? `memory-grid-${memoryGrid}`
                    : `${difficulty}-grid`
              }`}>
                {cards.map((value, index) => (
                  <Card
                    key={index}
                    value={value}
                    isFlipped={flipped.includes(index) || matched.includes(index)}
                    onClick={() => !matched.includes(index) && !flipped.includes(index) && handleClick(index)}
                    status={
                      matched.includes(index)
                        ? "matched"
                        : flipped.includes(index) && flipped.length === 2 && cards[flipped[0]] !== cards[flipped[1]]
                          ? "unmatched"
                          : ""
                    }
                  />
                ))}
              </div>
              {isDailyChallenge && (
                <div className="move-counter">
                  <h3>{t('game:moves', { count: moveCount })}</h3>
                </div>
              )}
              <div
                className={`timer ${
                  memoryMode
                    ? "memory-mode-timer"
                    : (difficulty === "hard" || (isStoryMode && storyLevelConfig && (storyLevelConfig.constraintMode === 'time' || storyLevelConfig.constraintMode === 'movesAndTime')))
                      ? "hard-mode-timer"
                      : difficulty === "medium"
                        ? "medium-mode-timer"
                        : "easy-mode-timer"
                }`}
                style={showDebugControls ? { left: `${debugTimerPosition.left}px`, top: `${debugTimerPosition.top}px` } : {}}
              >
                {(difficulty === "hard" || (isStoryMode && storyLevelConfig && (storyLevelConfig.constraintMode === 'time' || storyLevelConfig.constraintMode === 'movesAndTime'))) && (
                  <h3 className="time-remaining">{t('game:timer.remaining')}</h3>
                )}
                {/* Timer Display */}
                <h2
                  className={`timer ${
                    (difficulty === "hard" || (isStoryMode && storyLevelConfig && (storyLevelConfig.constraintMode === 'time' || storyLevelConfig.constraintMode === 'movesAndTime'))) &&
                    formatTime(Math.max(0, timeRemaining), true).isRed
                      ? "timer-red"
                      : ""
                  }`}
                >
                  {(difficulty === "hard" || (isStoryMode && storyLevelConfig && (storyLevelConfig.constraintMode === 'time' || storyLevelConfig.constraintMode === 'movesAndTime')))
                    ? `${formatTime(Math.max(0, timeRemaining), true).mainPart}.`
                    : `${formatTime(Math.max(0, elapsedTime)).mainPart}.`}
                  <span className="milliseconds">
                    {(difficulty === "hard" || (isStoryMode && storyLevelConfig && (storyLevelConfig.constraintMode === 'time' || storyLevelConfig.constraintMode === 'movesAndTime')))
                      ? formatTime(Math.max(0, timeRemaining), true).millisPart
                      : formatTime(Math.max(0, elapsedTime)).millisPart}
                  </span>
                </h2>

                {/* Best Time Display - show for all modes during gameplay */}
                {bestTime !== null && (
                  <h2
                    className={`best-time ${isDailyChallenge ? "daily-challenge-best-time" : ""}`}
                    dangerouslySetInnerHTML={{ __html: formatBestTime(bestTime) }}
                  />
                )}
              </div>
            </div>
          )}

          {gameLost && (
            <div className="lose-message-container">
              <div className="loser-emoji">{isStoryMode ? '😅' : t('game:loss.emoji')}</div>
              <h2>{isStoryMode ? `Level ${storyLevelIndex} Failed` : t('game:loss.heading')}</h2>
              <h3 style={{ fontFamily: "Groteska, sans-serif", fontSize: "12px" }}>
                {isStoryMode
                  ? (storyLevelConfig?.constraintMode === 'moves' || storyLevelConfig?.constraintMode === 'movesAndTime'
                      ? `Out of moves! (${moveCount}/${storyLevelConfig.maxMoves})`
                      : 'Out of time!')
                  : t('game:loss.subheading')}
              </h3>
              <button onClick={initializeGame} className="try-again-btn">
                {isStoryMode ? 'Retry Level' : t('game:loss.tryAgain')}
              </button>
              {isStoryMode ? (
                <>
                  <button
                    onClick={() => {
                      setIsStoryMode(false)
                      setStoryLevelConfig(null)
                      setGameStarted(false)
                      setCompletionTime(null)
                      setGameComplete(false)
                                      setShowStoryLevelPicker(true)
                    }}
                    className="share-btn"
                  >
                    Level Select
                  </button>
                  <button
                    onClick={() => {
                      setIsStoryMode(false)
                      setStoryLevelConfig(null)
                      setGameStarted(false)
                      setCompletionTime(null)
                      setGameComplete(false)
                                      setShowModal(true)
                    }}
                    className="leaderboard-btn"
                    style={{ marginTop: '10px' }}
                  >
                    Main Menu
                  </button>
                </>
              ) : (
                <button onClick={handleShare} className="share-btn">
                  {t('game:loss.shareResults')}
                </button>
              )}
              {showSharePanel && (
                <SharePanel
                  shareText={`MATCHA ❇️  
Moves: ${moveCount} | Time: ${Math.floor(completionTime / 60)}m ${Math.floor(completionTime % 60)}s | Mode: ${
                    isDailyChallenge
                      ? "Daily Challenge"
                      : memoryMode
                        ? `Memory Mode (${memoryGrid})`
                        : difficulty.charAt(0).toUpperCase() + difficulty.slice(1)
                  }  
${moveHistory.join(" ")} ⏳  
Can you beat my score? Play here: https://www.matcha-game.com`}
                  url="https://www.matcha-game.com"
                  onClose={closeSharePanel}
                />
              )}
            </div>
          )}

          {showModal && (
            <Modal onClose={() => setShowModal(false)}>
              <div>
                <img src={logo || "/placeholder.svg"} alt={t('nav.logoAlt')} className="logo" onClick={initializeGame} />
                <h2 className="difficulty-heading">{t('difficulty.select')}</h2>
                <div className="difficulty-buttons">
                  {["easy", "medium", "hard"].map((level) => (
                    <button
                      key={level}
                      className="difficulty-btn"
                      onClick={() => {
                        setGameStarted(false) // Stop previous game
                        setGameComplete(false)
                        setDifficulty(level)
                        setIsDailyChallenge(false)
                        setMemoryMode(false)
                        setShowModal(false)

                        // Check if animated tutorial has been shown this visit
                        const tutorialKey = `animatedTutorial_${level}`
                        const hasSeenTutorial = seenTutorials.has(tutorialKey)

                        if (!hasSeenTutorial) {
                          seenTutorials.add(tutorialKey)
                          const gridSizes = {
                            easy: { rows: 3, cols: 4 },
                            medium: { rows: 4, cols: 4 },
                            hard: { rows: 4, cols: 4 }
                          }
                          const constraints = level === 'hard'
                            ? { moves: null, timeLimit: 30 }
                            : { moves: null, timeLimit: null }

                          setShowAnimatedTutorial({
                            mode: level,
                            gridSize: gridSizes[level],
                            constraints
                          })
                        } else {
                          // Start game directly
                          initializeGame()
                        }
                      }}
                    >
                      {t(`difficulty.${level}`)}
                    </button>
                  ))}
                </div>
                <hr className="divider" />
                <button
                  className="memory-mode-btn"
                  onClick={() => {
                    setShowModal(false)
                    setShowMemoryModal(true)
                    setIsDailyChallenge(false)
                  }}
                >
                  {t('modes.memory')}
                </button>
                <hr className="divider" />
                <div className="speedrun-toggle">
                  <input
                    type="checkbox"
                    id="speedrun-toggle-checkbox"
                    checked={speedrunMode}
                    onChange={() => setSpeedrunMode(!speedrunMode)}
                  />
                  <label className="speedrun-label" htmlFor="speedrun-toggle-checkbox">
                    {t('modes.speedrun')}
                  </label>
                </div>
                <hr className="divider" />
                <button
                  className="daily-challenge-btn"
                  onClick={() => {
                    // Check if player has already played today
                    if (isDailyChallengeAvailable()) {
                      setDifficulty("medium")
                      setIsDailyChallenge(true)
                      localStorage.setItem("wasPlayingDailyChallenge", "true")
                      setShowModal(false)
                      initializeGame()
                    } else {
                      // Show previous results instead of starting a new game
                      setShowModal(false)

                      // Load today's score data from localStorage
                      const today = new Date().toISOString().split("T")[0]
                      const dailyScores = JSON.parse(localStorage.getItem("dailyScores") || "{}")
                      const todayScore = dailyScores[today]

                      if (todayScore) {
                        // Set viewing flag
                        localStorage.setItem("viewingDailyResults", "true")

                        // Directly set all the necessary state values
                        setCompletionTime(todayScore.time)

                        // Load move history if available
                        const savedMoveHistory = JSON.parse(localStorage.getItem("dailyMoveHistory") || "[]")
                        setMoveHistory(savedMoveHistory.length > 0 ? savedMoveHistory : [])
                        
                        // Fallback for old daily challenges: use moveHistory.length if moves is 0 or undefined
                        const storedMoves = todayScore.moves || 0
                        const moves = storedMoves > 0 ? storedMoves : savedMoveHistory.length
                        setMoveCount(moves)

                        // Load streak
                        const currentStreak = Number(localStorage.getItem("dailyStreak") || "1")
                        setDailyStreak(currentStreak)

                        // Set the daily challenge flag to ensure proper UI rendering
                        setIsDailyChallenge(true)

                        // Show the daily results directly
                        setShowDailyResults(true)

                        if (process.env.NODE_ENV === "development") console.log("Showing daily results for today's completed challenge")
                      }
                    }
                  }}
                >
                  {isDailyChallengeAvailable() ? "Daily Challenge" : "View Today's Results"}
                </button>

                {/* Story Mode Button */}
                {isFeatureEnabled('storyModeBeta') && (
                  <>
                    <hr className="divider" />
                    <button
                      className="story-mode-btn"
                      onClick={() => {
                        setShowModal(false)
                        // Clear previous game state before opening level picker
                        setCompletionTime(null)
                        setGameComplete(false)
                                          setShowStoryLevelPicker(true)
                      }}
                    >
                      Story Mode
                      <span className="beta-tag">BETA</span>
                    </button>
                  </>
                )}

                {/* Add signup link */}
                <div className="signup-link-container">
                  <button className="signup-link" onClick={handleDifficultyModalSignupClick}>
                    Signup to know when there's a new daily challenge
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {showMemoryModal && (
            <Modal onClose={() => setShowMemoryModal(false)}>
              <div>
                <h2>Select Grid Size</h2>
                <div className="memory-grid-selection">
                  {["4x4", "4x5", "4x6"]
                    .concat(window.innerWidth > 768 ? ["4x7"] : []) // Only add 4x7 if NOT on mobile
                    .map((size) => (
                      <button
                        key={size}
                        className="memory-grid-btn"
                        onClick={() => {
                          setMemoryGrid(size)
                          setMemoryMode(true)
                          setDifficulty('medium')
                          setIsDailyChallenge(false)
                          setShowMemoryModal(false)

                          // Check if animated tutorial has been shown this visit
                          const tutorialKey = `animatedTutorial_memory_${size}`
                          const hasSeenTutorial = seenTutorials.has(tutorialKey)

                          if (!hasSeenTutorial) {
                            seenTutorials.add(tutorialKey)
                            const [rows, cols] = size.split('x').map(Number)
                            setShowAnimatedTutorial({
                              mode: 'memory',
                              gridSize: { rows, cols },
                              constraints: { moves: null, timeLimit: null }
                            })
                          } else {
                            // Start game directly
                            initializeGame()
                          }
                        }}
                      >
                        {size}
                      </button>
                    ))}
                </div>
              </div>
            </Modal>
          )}

          {/* Story Level Picker Modal */}
          {showStoryLevelPicker && (
            <StoryLevelPicker
              onClose={() => setShowStoryLevelPicker(false)}
              onSelectLevel={handleStoryModeStart}
            />
          )}


          {/* Pause modal */}
          {showPauseModal && (
            <Modal onClose={resumeGame}>
              <div className="pause-modal-container">
                <h2>Game Paused</h2>
                <div className="pause-buttons">
                  <button onClick={resumeGame} className="pause-btn">
                    Resume Game
                  </button>
                  <button
                    onClick={() => {
                      setShowPauseModal(false)
                      setShowModal(true)
                    }}
                    className="pause-btn"
                  >
                    New Game
                  </button>
                  <button
                    onClick={() => {
                      setShowPauseModal(false)
                      restartGame()
                    }}
                    className="pause-btn"
                  >
                    Restart
                  </button>
                  <button onClick={exitGame} className="pause-btn">
                    Exit Game
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {/* Win message for regular game and story mode */}
          {(gameComplete ||
            (matched.length === cards.length && cards.length > 0 && !isDailyChallenge && completionTime !== null)) && (
            <div className="win-message-container" style={{ zIndex: 2000, position: "fixed" }}>
              <div className="fire-emoji">{isStoryMode ? '🎯' : '🔥'}</div>
              <h2>
                {isStoryMode
                  ? `Level ${storyLevelIndex} Complete!`
                  : bestTime !== null && completionTime < bestTime
                    ? "New Best!"
                    : "You're on Fire!"}
              </h2>
              <div className="subheader">
                <div className="time-container">
                  <span className="time-value">
                    {formatTime(completionTime * 1000).mainPart}.
                    <span className="milliseconds">{formatTime(completionTime * 1000).millisPart}</span>
                  </span>
                  <span className="time-label">Completion Time</span>
                </div>
                {bestTime !== null && (
                  <div className="time-container">
                    <span className="time-value" dangerouslySetInnerHTML={{ __html: formatBestTime(bestTime) }} />
                    <span className="time-label">Best Time</span>
                  </div>
                )}
              </div>

              {/* Story Mode Buttons */}
              {isStoryMode ? (
                <>
                  <button
                    onClick={() => {
                      // Complete level and unlock next
                      completeLevel()

                      // Check if there's a next level
                      if (storyLevelIndex < 30) {
                        const nextLevel = storyLevelIndex + 1
                        setCurrentLevelIndex(nextLevel)
                        handleStoryModeStart(nextLevel)
                      } else {
                        // Completed all levels - return to level picker
                        setIsStoryMode(false)
                        setStoryLevelConfig(null)
                        setGameStarted(false)
                        setCompletionTime(null)
                        setGameComplete(false)
                                          setShowStoryLevelPicker(true)
                      }
                    }}
                    className="play-again-btn"
                  >
                    {storyLevelIndex < 30 ? 'Next Level' : 'Complete!'}
                  </button>
                  <button onClick={initializeGame} className="share-btn">
                    Replay Level
                  </button>
                  <button
                    onClick={() => {
                      setIsStoryMode(false)
                      setStoryLevelConfig(null)
                      setGameStarted(false)
                      setCompletionTime(null)
                      setGameComplete(false)
                                      setShowStoryLevelPicker(true)
                    }}
                    className="leaderboard-btn"
                  >
                    Level Select
                  </button>
                  <button
                    onClick={() => {
                      setIsStoryMode(false)
                      setStoryLevelConfig(null)
                      setGameStarted(false)
                      setCompletionTime(null)
                      setGameComplete(false)
                                      setShowModal(true)
                    }}
                    className="share-btn"
                    style={{ marginTop: '10px' }}
                  >
                    Main Menu
                  </button>
                </>
              ) : (
                <>
                  <button onClick={initializeGame} className="play-again-btn">
                    Play Again
                  </button>
                  <button onClick={handleShare} className="share-btn">
                    {showSharePanel ? "Hide Share Options" : "Share Results"}
                  </button>
                  <button onClick={() => openLeaderboardModal(false)} className="leaderboard-btn">
                    View Leaderboard
                  </button>
                </>
              )}
              {showSharePanel && (
                <SharePanel
                  shareText={`MATCHA ❇️  
Moves: ${moveCount} | Time: ${Math.floor(completionTime / 60)}m ${Math.floor(completionTime % 60)}s | Mode: ${
                    isDailyChallenge
                      ? "Daily Challenge"
                      : memoryMode
                        ? `Memory Mode (${memoryGrid})`
                        : difficulty.charAt(0).toUpperCase() + difficulty.slice(1)
                  }  
${moveHistory.join(" ")}  
Can you beat my score? Play here: https://www.matcha-game.com`}
                  url="https://www.matcha-game.com"
                  onClose={closeSharePanel}
                />
              )}
            </div>
          )}

          {/* Daily challenge results */}
          {showDailyResults && completionTime !== null && (
            <div className="daily-results-container" style={{ zIndex: 2500, position: "fixed" }}>
              <div className="fire-emoji">🏆</div>
              <h2>Daily Challenge Complete!</h2>
              <div className="subheader">
                <div className="time-container">
                  <span className="time-value-moves">{moveCount}</span>
                  <span className="time-label">Moves</span>
                </div>
                <div className="time-container">
                  <span className="time-value">
                    {formatTime(completionTime * 1000).mainPart}.
                    <span className="milliseconds">{formatTime(completionTime * 1000).millisPart}</span>
                  </span>
                  <span className="time-label">Time</span>
                </div>
                <div className="time-container">
                  <span className="time-value-streak">{dailyStreak}🔥</span>
                  <span className="time-label">Day Streak </span>
                </div>
              </div>
              <button
                onClick={() => {
                  // Clear all game state to properly return to the main menu
                  setShowDailyResults(false)
                  setGameStarted(false)
                  setGameComplete(false)
                  setIsDailyChallenge(false)
                  setCompletionTime(null)
                  setMoveCount(0)
                  setMoveHistory([])

                  // Force a state refresh by updating the version
                  setDailyResultsVersion((prev) => prev + 1)

                  // Clear any localStorage flags that might interfere
                  localStorage.removeItem("wasPlayingDailyChallenge")
                  localStorage.removeItem("viewingDailyResults")

                  if (process.env.NODE_ENV === "development") console.log("Returning to main menu from daily challenge results")
                }}
                className="play-again-btn"
              >
                Back to Menu
              </button>
              <button onClick={handleShare} className="share-btn">
                {showSharePanel ? "Hide Share Options" : "Share Results"}
              </button>
              <button onClick={() => openLeaderboardModal(false)} className="leaderboard-btn">
                View Daily Leaderboard
              </button>
              {showSharePanel && (
                <SharePanel
                  shareText={`MATCHA ❇️  
Moves: ${moveCount} | Time: ${Math.floor(completionTime / 60)}m ${Math.floor(completionTime % 60)}s | Mode: Daily Challenge  
${moveHistory.join(" ")}  
Can you beat my score? Play here: www.matcha-game.com`}
                  url="https://www.matcha-game.com"
                  onClose={closeSharePanel}
                />
              )}
            </div>
          )}

          <footer>
            {/* Language Switcher */}
            <div style={{ marginBottom: "16px" }}>
              <LanguageSwitcher isMobile={true} />
            </div>

            <p>
              {t('footer.copyright')}{" "}
              <a href="https://www.hisavage.com" target="_blank" rel="noopener noreferrer">
                Savage
              </a>{" "}
              |{" "}
              <a href="#" onClick={handleSignupClick}>
                {t('footer.signupLink')}
              </a>{" "}
              <a href="https://buymeacoffee.com/edbhit6dab" target="_blank" rel="noopener noreferrer">
                | {t('footer.supportLink')}
              </a>
              {process.env.NODE_ENV === "development" && (
                <button
                  onClick={resetMailchimpModal}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#666",
                    fontSize: "10px",
                    padding: "0 5px",
                    cursor: "pointer",
                    opacity: 0.5,
                  }}
                >
                  {t('footer.resetModal')}
                </button>
              )}
            </p>
          </footer>
          {showMailchimpModal && <MailchimpModal onClose={() => setShowMailchimpModal(false)} />}

          {/* Animated Tutorial Modal - All Modes */}
          {showAnimatedTutorial && (
            <AnimatedTutorial
              mode={showAnimatedTutorial.mode}
              gridSize={showAnimatedTutorial.gridSize}
              constraints={showAnimatedTutorial.constraints}
              onClose={() => {
                // Mark tutorial as seen for this visit (in-memory, resets on page close)
                const tutorialKey = showAnimatedTutorial.mode === 'story'
                  ? `animatedTutorial_story_${showAnimatedTutorial.levelIndex}`
                  : showAnimatedTutorial.mode === 'memory'
                    ? `animatedTutorial_memory_${showAnimatedTutorial.gridSize.rows}x${showAnimatedTutorial.gridSize.cols}`
                    : `animatedTutorial_${showAnimatedTutorial.mode}`

                seenTutorials.add(tutorialKey)
                setShowAnimatedTutorial(null)

                // Start the game directly (no more text instructions)
                initializeGame()
              }}
            />
          )}

          {/* OLD Story Mode Tutorial Modal - Keep for fallback */}
          {showStoryTutorial && (
            <Modal
              onClose={() => {
                const tutorialKey = `storyTutorial_${showStoryTutorial}`
                seenTutorials.add(tutorialKey)
                setShowStoryTutorial(null)
                initializeGame()
              }}
            >
              <div style={{ padding: '2rem', maxWidth: '500px' }}>
                {showStoryTutorial === 1 && (
                  <>
                    <h2 style={{ marginBottom: '1rem', color: '#01c482' }}>🎮 How to Play</h2>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                      Match all pairs of cards to complete each level!
                    </p>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                      <strong>• Preview:</strong> Cards will show for 1 second - memorize their positions!
                    </p>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                      <strong>• Match:</strong> Click two cards to flip them. If they match, they stay flipped!
                    </p>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                      <strong>• Progress:</strong> Complete levels to unlock the next ones.
                    </p>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: '#01c482' }}>
                      💡 Tip: Pay attention during the preview - it's your key to success!
                    </p>
                  </>
                )}
                {showStoryTutorial === 6 && (
                  <>
                    <h2 style={{ marginBottom: '1rem', color: '#01c482' }}>🎯 New Challenge: Move Limit</h2>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                      Starting from this level, you'll need to complete the game within a limited number of moves!
                    </p>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                      Watch the move counter at the top of the screen. If you exceed the maximum moves, the level will fail.
                    </p>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: '#01c482' }}>
                      💡 Tip: Try to memorize card positions during the preview to make better matches!
                    </p>
                  </>
                )}
                {showStoryTutorial === 15 && (
                  <>
                    <h2 style={{ marginBottom: '1rem', color: '#01c482' }}>⏱️ New Challenge: Time Limit</h2>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                      Now you're facing both move AND time limits! You must complete the level before time runs out.
                    </p>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                      The timer will count down at the top of the screen. When it reaches zero, the level fails.
                    </p>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: '#01c482' }}>
                      💡 Tip: Work quickly but carefully - rushing leads to mistakes!
                    </p>
                  </>
                )}
                <button
                  onClick={() => {
                    const tutorialKey = `storyTutorial_${showStoryTutorial}`
                    seenTutorials.add(tutorialKey)
                    setShowStoryTutorial(null)
                    initializeGame()
                  }}
                  style={{
                    marginTop: '1.5rem',
                    padding: '12px 32px',
                    background: '#01c482',
                    color: 'black',
                    border: 'none',
                    borderRadius: '50px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  Got it! Start Level
                </button>
              </div>
            </Modal>
          )}

          {/* Add this near the end of the component, before the closing return tag */}
          {showLeaderboardModal && (
            <LeaderboardModal
              onClose={() => {
                setShowLeaderboardModal(false)
                // If the game was paused when opening leaderboard, unpause it
                if (gamePaused && !completionTime && !showDailyResults) {
                  const pauseDuration = new Date() - pauseStartTime
                  setTotalPausedTime((prev) => prev + pauseDuration)
                  setGamePaused(false)
                }
              }}
              mode={isDailyChallenge ? "daily" : memoryMode ? "memory" : "standard"}
              isDailyChallenge={isDailyChallenge}
              memoryMode={memoryMode}
              difficulty={difficulty}
              moveCount={moveCount}
              completionTime={completionTime}
              formatTime={formatTime}
              // Use the leaderboardViewOnly state to determine if it's view-only
              viewOnly={leaderboardViewOnly || checkIfGameSubmitted()}
              alreadySubmitted={checkIfGameSubmitted()}
            />
          )}

          {/* Debug Timer Position Controls */}
          {showDebugControls && (
            <div style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              background: 'rgba(0, 0, 0, 0.9)',
              border: '2px solid #01c482',
              borderRadius: '12px',
              padding: '20px',
              zIndex: 10000,
              color: '#fffbe8',
              fontFamily: 'monospace',
              minWidth: '300px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, color: '#01c482' }}>Timer Position Debug</h3>
                <button
                  onClick={() => setShowDebugControls(false)}
                  style={{
                    background: '#ff3b30',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>
                  Left: {debugTimerPosition.left}px
                </label>
                <input
                  type="range"
                  min="-500"
                  max="1500"
                  value={debugTimerPosition.left}
                  onChange={(e) => setDebugTimerPosition(prev => ({ ...prev, left: parseInt(e.target.value) }))}
                  style={{ width: '100%' }}
                />
                <input
                  type="number"
                  value={debugTimerPosition.left}
                  onChange={(e) => setDebugTimerPosition(prev => ({ ...prev, left: parseInt(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    marginTop: '5px',
                    padding: '5px',
                    background: '#1a1a1a',
                    border: '1px solid #01c482',
                    color: '#fffbe8',
                    borderRadius: '5px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>
                  Top: {debugTimerPosition.top}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="800"
                  value={debugTimerPosition.top}
                  onChange={(e) => setDebugTimerPosition(prev => ({ ...prev, top: parseInt(e.target.value) }))}
                  style={{ width: '100%' }}
                />
                <input
                  type="number"
                  value={debugTimerPosition.top}
                  onChange={(e) => setDebugTimerPosition(prev => ({ ...prev, top: parseInt(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    marginTop: '5px',
                    padding: '5px',
                    background: '#1a1a1a',
                    border: '1px solid #01c482',
                    color: '#fffbe8',
                    borderRadius: '5px'
                  }}
                />
              </div>

              <div style={{
                background: '#1a1a1a',
                padding: '10px',
                borderRadius: '5px',
                fontSize: '11px',
                marginTop: '15px',
                border: '1px solid #01c482'
              }}>
                <div>📋 Copy these values:</div>
                <div style={{ marginTop: '5px', color: '#01c482', fontWeight: 'bold' }}>
                  left: {debugTimerPosition.left}px;
                </div>
                <div style={{ color: '#01c482', fontWeight: 'bold' }}>
                  top: {debugTimerPosition.top}px;
                </div>
              </div>
            </div>
          )}
    </div>
  )
}

export default function AppWrapper() {
  return (
    <LeaderboardProvider>
      <StoryModeProvider>
        <Routes>
          <Route path="/" element={<App />} />
          <Route path="/tutorial" element={<TutorialPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/playtest" element={<Playtest />} />
        </Routes>
      </StoryModeProvider>
    </LeaderboardProvider>
  )
}